import * as Yup from "yup";
import { DateTime } from "luxon";

import { createForm } from "../../forms";
import { cents } from "../../../utils";
import Filters from "../../../../filters";

import {
  getAmountAvailableByTransactionTypeAsFloat,
  getStartDate,
} from "./utils";

import type Account from "../../../../Account";
import type { TypeSafeReactLocalization } from "../../../composites";

export const MAX_MEMO_LENGTH = 128;
export const MAX_ENTRY_DESC_LENGTH = 10;

export type LimitsForDisplayType = {
  [key: string]: {
    [key: string]: any;
  };
};

export type ACHPaymentFormBaseType = {
  fromAccount: string;
  secCode: string;
  memo?: string;
  entryDesc: string;
};

// TODO: just placeholder
export type ACHDeliverySpeed = "" | "standard" | "instant" | "sameDay";

export type ACHPaymentFormType = ACHPaymentFormBaseType & {
  amount: Cents;
  transactionType: API.CreateScheduledACHPayment.Request["transaction_type"];
  description?: string;
  deliverySpeed: ACHDeliverySpeed;
  // distinguishes a genuine member choice of deliverySpeed from the form's default
  // value, since "standard" is itself a valid ACHDeliverySpeed
  deliverySpeedManuallySelected?: boolean;
  deliverySpeedRemovedNotice?: Extract<ACHDeliverySpeed, "instant" | "sameDay">;
  startDate: string;
  deviceId: string;
  deviceRequestId: string;
  invoice?: string;

  recurringRule?: {
    isVisible: boolean;
    ends: "" | "never" | "on" | "after";
    frequency?: string;
    untilDate?: string;
    count?: string;
  };

  "recurringRule.isVisible"?: boolean;
  "recurringRule.ends"?: string;
  "recurringRule.frequency"?: string;
  "recurringRule.untilDate"?: string;
  "recurringRule.count"?: number;
};

export type AchScheduledPaymentValidationSchemaProps = {
  limitsForDisplay: Partial<Record<API.Limits.Type, API.Limits.Limit>>;
  values: ACHPaymentFormType;
  isOneTimePaymentForToday: boolean;
  achUseAvailableBalanceForScheduledPush: boolean;
  isBusinessUser: boolean;
  accounts: Account[];
  isInstantDelivery?: boolean;
  // flows that collect the recipient-facing memo (`description`) on the
  // payment details step rather than leaving it optional, e.g. invoice pay
  shouldValidateDescription?: boolean;
  // flows that collect the internal note (memo) on the payment details step
  // rather than on a later step, e.g. invoice pay
  shouldValidateInternalNote?: boolean;
  // flows that collect the additional details (SEC code, entry description) on
  // the payment details step rather than on a later step, e.g. invoice pay
  shouldValidateAdditionalDetails?: boolean;
};

export const achPaymentBaseSchema = ({
  getString,
}: TypeSafeReactLocalization) =>
  Yup.object().shape({
    fromAccount: Yup.string().required(
      getString("form-generic-validation-required"),
    ),
    transactionType: Yup.string().required(
      getString("form-generic-validation-required"),
    ),
    amount: Yup.number()
      .integer(getString("form-generic-validation-number"))
      .positive(getString("form-generic-validation-positive-number"))
      .required(getString("form-generic-validation-required")),
    description: Yup.string().max(
      MAX_MEMO_LENGTH,
      `Memo must be less than ${MAX_MEMO_LENGTH} characters`,
    ),
    secCode: Yup.string().required(
      getString("form-generic-validation-required"),
    ),
    deviceId: Yup.string().optional(),
    deviceRequestId: Yup.string().optional(),
  });

export const scheduledPaymentBaseSchema = ({
  getString,
}: TypeSafeReactLocalization) =>
  Yup.object().shape({
    secCode: Yup.string().required(
      getString("form-generic-validation-required"),
    ),
    memo: Yup.string().max(MAX_MEMO_LENGTH),
    entryDesc: Yup.string()
      .max(MAX_ENTRY_DESC_LENGTH)
      .required(getString("form-generic-validation-required")),
  });

// For ACH Push immediate one-time transfer, first validate based on ACH push limit
// Then validate against available balance if transfer is immediate one-time or if transfer is scheduled and achUseAvailableBalanceForScheduledPush is enabled
// It is possible for biz users to also have sub-user ACH Push limits, so check that as well
export const getValidateACHPushTransferErrorMessage =
  (props: AchScheduledPaymentValidationSchemaProps) =>
  ({ getString }: TypeSafeReactLocalization) => {
    const {
      limitsForDisplay,
      values,
      isOneTimePaymentForToday,
      achUseAvailableBalanceForScheduledPush,
      isBusinessUser,
      accounts,
    } = props;
    const limitsAsFloat =
      getAmountAvailableByTransactionTypeAsFloat(limitsForDisplay);
    const dollarAmount = Filters.penniesToDollars(values.amount);

    const fromAccount = accounts.find(
      (a: Account) => a.id === values.fromAccount,
    );

    const productType = fromAccount?.product_type;
    const productScopedKey = productType
      ? `unverified_ach_push.${productType}`
      : null;
    const orgProductScopedKey = productType
      ? `organization_user_unverified_ach_push.${productType}`
      : null;

    const hasProductScopedLimit =
      productScopedKey && productScopedKey in limitsAsFloat;
    const hasOrgProductScopedLimit =
      orgProductScopedKey && orgProductScopedKey in limitsAsFloat;
    const hasBaseLimits =
      "unverified_ach_push" in limitsAsFloat ||
      "organization_user_unverified_ach_push" in limitsAsFloat;

    if (!hasProductScopedLimit && !hasOrgProductScopedLimit && !hasBaseLimits) {
      // if the limit isn't defined for this user, allow them to proceed
      return "";
    }

    // Collect applicable limits, preferring product-scoped limits over base limits
    const applicableLimits: number[] = [];

    if (hasProductScopedLimit) {
      applicableLimits.push(limitsAsFloat[productScopedKey]);
    } else if (
      limitsAsFloat.unverified_ach_push !== undefined &&
      limitsAsFloat.unverified_ach_push !== null
    ) {
      applicableLimits.push(limitsAsFloat.unverified_ach_push);
    }

    if (hasOrgProductScopedLimit) {
      applicableLimits.push(limitsAsFloat[orgProductScopedKey]);
    } else if (
      limitsAsFloat.organization_user_unverified_ach_push !== undefined &&
      limitsAsFloat.organization_user_unverified_ach_push !== null
    ) {
      applicableLimits.push(
        limitsAsFloat.organization_user_unverified_ach_push,
      );
    }

    const achPushLimit = Math.min(...applicableLimits);

    // ensure the error message accurately reflects which limit is being applied
    if (isOneTimePaymentForToday && dollarAmount > achPushLimit) {
      const limit = Filters.currency(achPushLimit as Dollars, {
        hasDecimal: true,
      });
      return isBusinessUser &&
        limitsAsFloat.unverified_ach_push === achPushLimit
        ? getString("ach-payment-form-limit-exceeded-business", {
            limit,
          })
        : getString("ach-payment-form-limit-exceeded-personal", {
            limit,
          });
    }
    if (
      fromAccount &&
      (isOneTimePaymentForToday || achUseAvailableBalanceForScheduledPush)
    ) {
      if (dollarAmount > fromAccount.transferableBalanceAsFloat()) {
        return getString("ach-payment-form-insufficient-funds");
      }
    }
    return "";
  };

const getErrorMessageForACHPull =
  (props: AchScheduledPaymentValidationSchemaProps) =>
  ({ getString }: TypeSafeReactLocalization) => {
    const { limitsForDisplay: limits, values, isBusinessUser } = props;
    const amountsAvailable = getAmountAvailableByTransactionTypeAsFloat(limits);
    const dollarAmount = Filters.penniesToDollars(values.amount);
    const {
      unverified_ach_pull: orgPullLimit,
      organization_user_unverified_ach_pull: userPullLimit,
    } = amountsAvailable;
    const userOrOrgAmountAvailable = userPullLimit ?? orgPullLimit;
    const usingUserLimit = userPullLimit != null;

    if (
      isBusinessUser &&
      userOrOrgAmountAvailable != null &&
      dollarAmount > userOrOrgAmountAvailable
    ) {
      const formattedAmountAvailable = Filters.currency(
        userOrOrgAmountAvailable as Dollars,
        {
          hasDecimal: true,
        },
      );
      return getString(
        usingUserLimit
          ? "ach-payment-form-collect-limit-exceeded-personal"
          : "ach-payment-form-collect-limit-exceeded-business",
        {
          limit: formattedAmountAvailable,
        },
      );
    }

    return "";
  };

export const getAchScheduledPaymentDetailsSchema =
  (props: AchScheduledPaymentValidationSchemaProps) =>
  (l10n: TypeSafeReactLocalization) => {
    const { getString } = l10n;
    const {
      values,
      isInstantDelivery,
      shouldValidateDescription,
      shouldValidateInternalNote,
      shouldValidateAdditionalDetails,
    } = props;
    const isCollectPayment = values.transactionType === "debit";
    const amountErrorMessage = isCollectPayment
      ? getErrorMessageForACHPull(props)(l10n)
      : getValidateACHPushTransferErrorMessage(props)(l10n);

    const baseSchema = achPaymentBaseSchema(l10n)
      .pick(["fromAccount", "description"])
      .concat(
        Yup.object().shape({
          amount: Yup.number()
            .integer(getString("form-generic-validation-number"))
            .positive(getString("form-generic-validation-positive-number"))
            .required(getString("form-generic-validation-required"))
            .test("test-amount-limits", amountErrorMessage, function (_value) {
              return !amountErrorMessage;
            }),
          description: shouldValidateDescription
            ? Yup.string()
                .max(
                  MAX_MEMO_LENGTH,
                  `Memo must be less than ${MAX_MEMO_LENGTH} characters`,
                )
                .required(getString("form-generic-validation-required"))
            : Yup.string().max(
                MAX_MEMO_LENGTH,
                `Memo must be less than ${MAX_MEMO_LENGTH} characters`,
              ),
          startDate: Yup.string().required(
            getString("form-generic-validation-required"),
          ),
          recurringRule: Yup.object().shape({
            isVisible: Yup.boolean(),
            ends: Yup.string(),
            frequency: Yup.string().when("isVisible", {
              is: true,
              then: (schema) =>
                schema.required(getString("form-generic-validation-required")),
            }),
            untilDate: Yup.string().when("ends", {
              is: "on",
              then: (schema) =>
                schema.required(getString("form-generic-validation-required")),
            }),
            count: Yup.number().when("ends", {
              is: "after",
              then: (schema) =>
                schema
                  .required(getString("form-generic-validation-required"))
                  .max(
                    99,
                    getString("form-generic-validation-limit-characters", {
                      count: 99,
                    }),
                  ),
            }),
          }),
        }),
      );

    // when instant delivery is selected we skip the additional details step,
    // so the internal note (memo) is collected and validated here on step 2
    const schemaWithInternalNote =
      isInstantDelivery || shouldValidateInternalNote
        ? baseSchema.concat(
            Yup.object().shape({
              memo: Yup.string().max(MAX_MEMO_LENGTH),
            }),
          )
        : baseSchema;

    // flows without a dedicated additional details step (e.g. invoice pay)
    // render those fields here, so apply the same rules the later step would
    if (shouldValidateAdditionalDetails) {
      return schemaWithInternalNote.concat(scheduledPaymentBaseSchema(l10n));
    }

    return schemaWithInternalNote;
  };

export const getAchScheduledAdditionalDetailsSchema =
  (existingSchema: Yup.Schema) => (l10n: TypeSafeReactLocalization) => {
    return existingSchema.concat(scheduledPaymentBaseSchema(l10n));
  };

const getOneYearFromNow = () => {
  const oneYearFromNow = DateTime.now().plus({ years: 1 });
  const formattedDate = oneYearFromNow.toFormat("MM/dd/yyyy");
  return formattedDate;
};

export const initialValues: ACHPaymentFormType = {
  fromAccount: "",
  amount: cents(0),
  transactionType: "credit",
  secCode: "",
  memo: "",
  description: "",
  entryDesc: "",
  deliverySpeed: "standard",
  deliverySpeedManuallySelected: false,
  deliverySpeedRemovedNotice: undefined,
  startDate: getStartDate(),
  deviceId: "",
  deviceRequestId: "",
  invoice: undefined,

  recurringRule: {
    isVisible: false,
    ends: "",
    frequency: "",
    untilDate: getOneYearFromNow(),
    count: "12",
  },
};

export const transformToApiFields = ({
  amount,
  fromAccount: institution_account,
  transactionType: transaction_type,
  achCompany: ach_company,
  secCode: std_ent_cls_code,
  entryDesc: entry_desc,
  deliverySpeed,
  recipient,
  description,
  memo,
  deviceId: device_id,
  deviceRequestId: device_request_id,
  invoice,
  deliverySpeedManuallySelected,
}: ACHPaymentFormType & {
  recipient: API.RecipientId;
  entryDesc: API.CreateACHPayment.Request["entry_desc"];
  achCompany: API.CreateACHPayment.Request["ach_company"];
  invoice?: API.CreateACHPayment.Request["invoice"];
  isSingleExpediteEnabled?: boolean;
}) => {
  return {
    amount,
    institution_account,
    transaction_type,
    ach_company,
    std_ent_cls_code,
    entry_desc,
    recipient,
    description,
    memo,
    device_id,
    device_request_id,
    invoice,
    // only send an explicit override once a delivery speed has actually been
    // surfaced to the user, otherwise let the API fall back to the institution's
    // global setting. deliberately not gated on `single_expedite`: invoice pay
    // offers expedited speeds off `same_day_ach`/`fednow_send` alone
    ...(deliverySpeedManuallySelected
      ? { is_same_day: deliverySpeed === "sameDay" }
      : {}),
  };
};

// instant delivery creates a FedNowTransaction instead of an ACH transaction, so it
// carries none of the ACH-only fields (SEC code, entry description, ACH company) and
// can never be scheduled. the recipient-facing memo is `description`
export const transformToFedNowApiFields = ({
  amount,
  fromAccount,
  recipient,
  description,
  deviceId,
  deviceRequestId,
  invoice,
}: ACHPaymentFormType & {
  recipient: API.RecipientId;
  invoice?: API.CreateFedNowPayment.Request["invoice"];
}): API.CreateFedNowPayment.Request => ({
  source_account: fromAccount as API.AccountId,
  unverified_recipient: recipient,
  amount_cents: amount,
  memo: description,
  invoice,
  device_id: deviceId || undefined,
  device_request_id: deviceRequestId || undefined,
});

export const transformToScheduledApiFields = (
  values: ACHPaymentFormType & {
    recipient: API.RecipientId;
    entryDesc: API.CreateACHPayment.Request["entry_desc"];
    achCompany: API.CreateACHPayment.Request["ach_company"];
    transactionType: API.CreateScheduledACHPayment.Request["transaction_type"];
    recurringRuleString: API.CreateScheduledACHPayment.Request["recurring_rule"];
    isSingleExpediteEnabled?: boolean;
  },
) => {
  const baseFields = transformToApiFields(values);
  const {
    transactionType: transaction_type,
    recurringRuleString: recurring_rule,
  } = values;

  return {
    ...baseFields,
    transaction_type,
    recurring_rule,
  };
};

export const ACHPaymentForm = createForm({
  initialValues,
  translateValidationSchema: achPaymentBaseSchema,
});
